import type { ReactNode } from 'react'
import { ChevronLeft, Flag, Lightbulb, MessageSquare, User, Users } from 'lucide-react'
import { tokens } from '@/theme'
import type { NavTab } from '@/domain/types'
import { TrelleborgMark } from './TrelleborgMark'
import { MOBILE_TABBAR_H } from './responsive'

/**
 * The phone shell: a dark bar with the page's name in it, an optional
 * segmented control for a section's pages, and the four tabs at the bottom
 * where a thumb reaches them. Desktop keeps SiteHeader and SubNav.
 */

const iconButton = (lit: boolean): React.CSSProperties => ({
  flex: 'none',
  width: 44,
  height: 44,
  display: 'grid',
  placeItems: 'center',
  background: lit ? tokens.inkPanel2 : 'transparent',
  border: 'none',
  borderRadius: tokens.radiusMd,
  color: lit ? tokens.paper : tokens.inkPanelText,
  cursor: 'pointer',
})

interface HeaderProps {
  eyebrow: string
  title: string
  /** Detail pages go back to their list; top-level pages show the mark instead. */
  onBack: (() => void) | null
  onHome: () => void
  /** True on My work, Settings and the bug page, which the account sheet holds. */
  accountActive: boolean
  /** Time-off requests waiting for a manager, shown on the account icon. */
  pendingCount: number
  bell: ReactNode
  onAccount: () => void
}

export function MobileHeader({
  eyebrow,
  title,
  onBack,
  onHome,
  accountActive,
  pendingCount,
  bell,
  onAccount,
}: HeaderProps) {
  return (
    <header
      style={{
        position: 'sticky',
        top: 0,
        zIndex: 100,
        background: tokens.inkPanel,
        borderBottom: `1px solid ${tokens.inkPanelLine}`,
        padding: '0 8px 0 4px',
        paddingTop: 'env(safe-area-inset-top)',
        display: 'flex',
        alignItems: 'center',
        gap: 4,
        minHeight: 56,
        fontFamily: tokens.fontDisplay,
      }}
    >
      {onBack ? (
        <button
          type="button"
          aria-label="Back"
          onClick={onBack}
          style={{ ...iconButton(false), color: tokens.paper }}
        >
          <ChevronLeft size={20} strokeWidth={1.8} />
        </button>
      ) : (
        <button type="button" aria-label="Keystone home" onClick={onHome} style={iconButton(false)}>
          <TrelleborgMark size={21} />
        </button>
      )}
      <div style={{ flex: 1, minWidth: 0, display: 'grid', gap: 2, paddingLeft: 4 }}>
        <div
          style={{
            fontSize: 9.5,
            fontWeight: tokens.weightDemi,
            letterSpacing: '0.2em',
            textTransform: 'uppercase',
            color: tokens.gold,
            lineHeight: 1,
          }}
        >
          {eyebrow}
        </div>
        <h1
          style={{
            margin: 0,
            fontSize: 17,
            fontWeight: tokens.weightDemi,
            color: tokens.paper,
            lineHeight: 1.2,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {title}
        </h1>
      </div>
      {bell}
      <button
        type="button"
        aria-label={pendingCount > 0 ? `Account, ${pendingCount} pending` : 'Account'}
        onClick={onAccount}
        style={{ ...iconButton(accountActive), position: 'relative' }}
      >
        <User size={18} strokeWidth={1.6} />
        {pendingCount > 0 && (
          <span aria-hidden="true" style={{ position: 'absolute', top: 6, right: 4 }}>
            <CountBadge n={pendingCount} size={16} />
          </span>
        )}
      </button>
    </header>
  )
}

export interface Segment {
  id: string
  label: string
  badge?: number
}

/** A section's pages as a segmented control under the header. */
export function MobileSegments({
  label,
  segments,
  active,
  onSelect,
}: {
  label: string
  segments: Segment[]
  active: string
  onSelect: (id: string) => void
}) {
  return (
    <nav
      aria-label={label}
      style={{
        padding: '10px 16px',
        borderBottom: `1px solid ${tokens.line}`,
        background: tokens.paper,
        fontFamily: tokens.fontDisplay,
      }}
    >
      <div
        style={{
          display: 'flex',
          gap: 4,
          padding: 3,
          background: tokens.segmentTrack,
          borderRadius: tokens.radiusLg,
          overflowX: 'auto',
          scrollbarWidth: 'none',
        }}
      >
        {segments.map(s => {
          const on = s.id === active
          return (
            <button
              key={s.id}
              type="button"
              aria-current={on ? 'page' : undefined}
              onClick={() => onSelect(s.id)}
              style={{
                flex: '1 0 auto',
                minHeight: 38,
                padding: '0 12px',
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 6,
                background: on ? tokens.paper : 'transparent',
                color: on ? tokens.ink : tokens.ink3,
                border: 'none',
                borderRadius: tokens.radiusMd,
                boxShadow: on ? '0 1px 2px rgba(0,0,0,0.12)' : 'none',
                fontFamily: tokens.fontDisplay,
                fontSize: 13,
                fontWeight: tokens.weightDemi,
                cursor: 'pointer',
                whiteSpace: 'nowrap',
              }}
            >
              {s.label}
              {s.badge ? <CountBadge n={s.badge} size={18} /> : null}
            </button>
          )
        })}
      </div>
    </nav>
  )
}

const TABS: { id: NavTab; label: string; icon: ReactNode }[] = [
  { id: 'chat', label: 'Chat', icon: <MessageSquare size={22} strokeWidth={1.6} /> },
  { id: 'ideas', label: 'Ideas', icon: <Lightbulb size={22} strokeWidth={1.6} /> },
  { id: 'initiatives', label: 'Initiatives', icon: <Flag size={22} strokeWidth={1.6} /> },
  { id: 'people', label: 'People', icon: <Users size={22} strokeWidth={1.6} /> },
]

export function MobileTabBar({
  active,
  onChange,
}: {
  active: NavTab
  onChange: (tab: NavTab) => void
}) {
  return (
    <nav
      aria-label="Main"
      className="keystone-tabbar"
      style={{
        position: 'fixed',
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: 100,
        display: 'grid',
        gridTemplateColumns: `repeat(${TABS.length}, 1fr)`,
        background: tokens.inkPanel,
        borderTop: `1px solid ${tokens.inkPanelLine}`,
        paddingBottom: 'env(safe-area-inset-bottom)',
        fontFamily: tokens.fontDisplay,
      }}
    >
      {TABS.map(t => {
        const on = t.id === active
        return (
          <button
            key={t.id}
            type="button"
            aria-current={on ? 'page' : undefined}
            onClick={() => onChange(t.id)}
            style={{
              position: 'relative',
              minHeight: MOBILE_TABBAR_H,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 5,
              background: 'none',
              border: 'none',
              color: on ? tokens.paper : tokens.inkPanelText2,
              cursor: 'pointer',
            }}
          >
            <span
              style={{
                position: 'absolute',
                top: 0,
                left: '28%',
                right: '28%',
                height: 2,
                background: on ? tokens.gold : 'transparent',
              }}
            />
            <span style={{ display: 'grid', placeItems: 'center' }}>{t.icon}</span>
            <span
              style={{
                fontSize: 10,
                fontWeight: tokens.weightDemi,
                letterSpacing: '0.1em',
                textTransform: 'uppercase',
              }}
            >
              {t.label}
            </span>
          </button>
        )
      })}
    </nav>
  )
}

function CountBadge({ n, size }: { n: number; size: number }) {
  return (
    <span
      aria-label={`${n} pending`}
      style={{
        minWidth: size,
        height: size,
        padding: '0 5px',
        boxSizing: 'border-box',
        display: 'inline-grid',
        placeItems: 'center',
        background: tokens.gold,
        color: tokens.paper,
        borderRadius: 999,
        fontSize: size > 16 ? 10.5 : 9.5,
        fontWeight: tokens.weightDemi,
      }}
    >
      {n}
    </span>
  )
}
